import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import ReportList from '../components/ReportList';
import DeletionRequestList from '../components/DeletionRequestList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getElementReports, getIssueReports } from '../lib/admin-api';
import { Loader2, Search, RefreshCw, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-sonner-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ElementReport, IssueReport } from '../types/admin';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/firestore';
import { Link } from 'react-router-dom';

// Define a type for account deletion requests
interface DeletionRequest {
  id: string;
  userId: string;
  userEmail: string;
  reason: string;
  timestamp: any;
  status: 'new' | 'in-progress' | 'resolved' | 'dismissed';
  type: 'account_deletion';
  reviewedBy?: string;
  reviewedAt?: any;
  notes?: string | any[];
}

const ReportManagement: React.FC = () => {
  const [elementReports, setElementReports] = useState<ElementReport[]>([]);
  const [issueReports, setIssueReports] = useState<IssueReport[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('element');
  const { toast } = useToast();

  // Calculate active report counts (excluding dismissed)
  const getActiveReportCount = (reports: any[]) => {
    return reports.filter((report) => report.status !== 'dismissed').length;
  };

  useEffect(() => {
    fetchReports();
  }, [activeTab]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      if (activeTab === 'element') {
        const result = await getElementReports();
        const reports = result.reports as ElementReport[];
        setElementReports(reports);
        return reports;
      } else if (activeTab === 'issue') {
        const result = await getIssueReports();
        const reports = result.reports as IssueReport[];
        setIssueReports(reports);
        return reports;
      } else if (activeTab === 'deletion') {
        // Fetch all help_reports and filter client-side
        // This avoids the need for a composite index
        const deletionRequestsQuery = query(collection(db, 'help_reports'));

        const snapshot = await getDocs(deletionRequestsQuery);
        // Filter for deletion requests after fetching
        const requests = snapshot.docs
          .filter((doc) => {
            const data = doc.data();
            return data.type === 'account_deletion';
          })
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as DeletionRequest[];

        console.log('Deletion requests:', requests); // Debug log
        setDeletionRequests(requests);
        return requests;
      }
      return [];
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reports',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // In a real implementation, you would call an API with the search query

    // For now, just reset and fetch all reports
    fetchReports();
  };

  const handleRefresh = async () => {
    await fetchReports();
  };

  const handleElementReportsRefresh = async () => {
    try {
      setLoading(true);
      const result = await getElementReports();
      const reports = result.reports as ElementReport[];
      setElementReports(reports);
      return reports;
    } catch (error) {
      console.error('Error fetching element reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to load element reports',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleIssueReportsRefresh = async () => {
    try {
      setLoading(true);
      const result = await getIssueReports();
      const reports = result.reports as IssueReport[];
      setIssueReports(reports);
      return reports;
    } catch (error) {
      console.error('Error fetching issue reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to load issue reports',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleDeletionRequestsRefresh = async () => {
    try {
      setLoading(true);
      const deletionRequestsQuery = query(collection(db, 'help_reports'));
      const snapshot = await getDocs(deletionRequestsQuery);
      const requests = snapshot.docs
        .filter((doc) => {
          const data = doc.data();
          return data.type === 'account_deletion';
        })
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as DeletionRequest[];

      setDeletionRequests(requests);
      return requests;
    } catch (error) {
      console.error('Error fetching deletion requests:', error);
      toast({
        title: 'Error',
        description: 'Failed to load deletion requests',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const filteredReports =
    activeTab === 'element'
      ? elementReports.filter(
          (report) =>
            report.elementName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            report.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            report.description?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : activeTab === 'issue'
      ? issueReports.filter(
          (report) =>
            report.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            report.description?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : deletionRequests.filter(
          (request) =>
            request.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            request.reason?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            request.userId?.toLowerCase().includes(searchQuery.toLowerCase())
        );

  return (
    <AdminLayout title="Report Management">
      <div className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <TabsList>
              <TabsTrigger value="element" className="relative">
                Element Reports
                {getActiveReportCount(elementReports) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
                    {getActiveReportCount(elementReports)}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="issue" className="relative">
                Issue Reports
                {getActiveReportCount(issueReports) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
                    {getActiveReportCount(issueReports)}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="deletion" className="relative">
                Account Deletion Requests
                {getActiveReportCount(deletionRequests) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
                    {getActiveReportCount(deletionRequests)}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button variant="outline" size="icon" onClick={handleSearch}>
                <Search className="h-4 w-4" />
                <span className="sr-only">Search</span>
              </Button>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          <TabsContent value="element" className="mt-6">
            {loading && elementReports.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <ReportList
                reports={filteredReports as ElementReport[]}
                onRefresh={handleElementReportsRefresh}
              />
            )}
          </TabsContent>

          <TabsContent value="issue" className="mt-6">
            {loading && issueReports.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <ReportList
                reports={filteredReports as IssueReport[]}
                onRefresh={handleIssueReportsRefresh}
              />
            )}
          </TabsContent>

          <TabsContent value="deletion" className="mt-6">
            {loading && deletionRequests.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <DeletionRequestList
                requests={filteredReports as DeletionRequest[]}
                onRefresh={handleDeletionRequestsRefresh}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default ReportManagement;
